import { useState, useEffect, useCallback } from 'react';
import { list } from 'api/GroupController';
import errorReporter from '@app/errorReporter';
import { message } from 'components';
import { UserController } from 'api';
import { useDebounce } from 'hooks';

export interface Group {
  id: string;
  name: string;
  type: string;
  userCount: number;
  createdAt: string;
  org: string;
  updatedAt: string;
  users: string[];
}

const useGetGroups = ({ shouldFetch = true, searchText = '', orgId = '' }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [allGroups, setAllGroups] = useState<Group[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);

  const debouncedSearch = useDebounce(searchText, 500);

  const fetchGroups = async () => {
    try {
      setIsLoading(true);
      const groupList = await list();
      setAllGroups(groupList);
    } catch (err) {
      errorReporter.error(err);
      message.error('Error loading groups');
    } finally {
      setIsLoading(false);
    }
  };

  const filterGroups = useCallback(
    async (text: string) => {
      try {
        setIsLoading(true);

        const { users } = await UserController.search({
          name: text,
          orgId,
          page: 0,
          excludeInternalOrgs: true,
        });

        const userIds = users.map(user => user.id);

        const matchByGroupName = allGroups.filter(group =>
          group.name.toLowerCase().trim().includes(text.toLowerCase().trim()),
        );
        const foundGroupIds = new Set(matchByGroupName.map(match => match.id));
        const matchByUser = allGroups.filter(
          group =>
            !foundGroupIds.has(group.id) &&
            userIds.some(userId => group.users.includes(userId!)),
        );

        setGroups([...matchByGroupName, ...matchByUser]);
      } catch (err) {
        errorReporter.error(err);
        message.error('Error searching groups');
      } finally {
        setIsLoading(false);
      }
    },
    [allGroups, orgId],
  );

  useEffect(() => {
    if (!shouldFetch) return;
    if (debouncedSearch) {
      filterGroups(debouncedSearch);
      return;
    }
    setGroups(allGroups);
  }, [shouldFetch, debouncedSearch, allGroups, filterGroups]);

  useEffect(() => {
    if (!shouldFetch) return;
    fetchGroups();
  }, [shouldFetch]);

  return { isLoading, groups, fetchGroups, groupsCount: groups.length };
};

export default useGetGroups;
